// projectlab-web/src/context/AuthContext.tsx
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { Session, User } from '@supabase/supabase-js'

import { supabase, logSupabaseError, getAuthConfirmUrl } from '@/lib/supabase'
import { createLogger } from '@/lib/logger'

const log = createLogger('AuthContext')

export type UserRole = 'talent' | 'owner' | 'admin'

export interface DBUser {
  id: string
  email: string
  role: UserRole
  full_name: string | null
  avatar_url: string | null
  phone: string | null
  is_verified_student: boolean
  created_at: string
  updated_at: string
}

export interface DBTalent {
  id: string
  user_id: string
  full_name: string | null
  headline: string | null
  bio: string | null
  skill_level: 'entry' | 'intermediate' | 'advanced' | 'professional'
  skills: string[]
  cv_url: string | null
  portfolio_url: string | null
  linkedin_url: string | null
  location: string | null
  availability: string | null
  terms_accepted: boolean
  terms_accepted_at: string | null
  created_at: string
  updated_at: string
}

export interface DBProjectOwner {
  id: string
  user_id: string
  company_name_en: string
  company_name_zh: string | null
  business_registration_no: string
  br_url: string | null
  br_expiry_date: string | null
  registered_address: string | null
  industry: string | null
  contact_name: string
  contact_email: string
  contact_phone: string | null
  company_website: string | null
  company_summary: string | null
  default_team_size: number | null
  estimated_monthly_projects: string | null
  is_verified: boolean
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
  updated_at: string
}

export interface AuthUser extends User {
  dbProfile?: DBUser
}

interface AuthContextType {
  user: AuthUser | null
  session: Session | null
  dbProfile: DBUser | null
  talentProfile: DBTalent | null
  ownerProfile: DBProjectOwner | null
  role: UserRole | null
  loading: boolean
  profileLoading: boolean
  hasProfile: boolean
  signInWithOtp: (email: string) => Promise<void>
  verifyOtp: (email: string, token: string) => Promise<void>
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function isIgnorableProfileError(error: unknown) {
  const code = (error as { code?: string } | null)?.code
  return code === 'PGRST116' || code === '42501'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [dbProfile, setDbProfile] = useState<DBUser | null>(null)
  const [talentProfile, setTalentProfile] = useState<DBTalent | null>(null)
  const [ownerProfile, setOwnerProfile] = useState<DBProjectOwner | null>(null)
  const [role, setRole] = useState<UserRole | null>(null)
  const [loading, setLoading] = useState(true)
  // Separate flag: true while role-specific profile row is being fetched
  const [profileLoading, setProfileLoading] = useState(true)

  const syncVersionRef = useRef(0)

  const hasProfile = (() => {
    if (!role) return false
    if (role === 'talent') return talentProfile !== null
    if (role === 'owner') return ownerProfile !== null
    return true
  })()

  const clearProfileState = useCallback(() => {
    setDbProfile(null)
    setTalentProfile(null)
    setOwnerProfile(null)
    setRole(null)
    setProfileLoading(false)
  }, [])

  const fetchProfile = useCallback(async (userId: string): Promise<DBUser | null> => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      if (!isIgnorableProfileError(error)) logSupabaseError('fetchProfile', error)
      return null
    }

    return (data as DBUser | null) ?? null
  }, [])

  const fetchTalentProfile = useCallback(async (userId: string): Promise<DBTalent | null> => {
    const { data, error } = await supabase
      .from('talents')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      if (!isIgnorableProfileError(error)) logSupabaseError('fetchTalentProfile', error)
      return null
    }

    return (data as DBTalent | null) ?? null
  }, [])

  const fetchOwnerProfile = useCallback(async (userId: string): Promise<DBProjectOwner | null> => {
    const { data, error } = await supabase
      .from('project_owners')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      if (!isIgnorableProfileError(error)) logSupabaseError('fetchOwnerProfile', error)
      return null
    }

    return (data as DBProjectOwner | null) ?? null
  }, [])

  const syncUser = useCallback(
    async (authUser: User | null, authSession: Session | null) => {
      const syncVersion = ++syncVersionRef.current
      setSession(authSession)

      if (!authUser) {
        setUser(null)
        clearProfileState()
        return
      }

      const enrichedUser: AuthUser = Object.assign(authUser, {})
      setUser(enrichedUser)

      // Mark profile as still loading while we fetch
      setProfileLoading(true)

      const profile = await fetchProfile(authUser.id)

      if (syncVersion !== syncVersionRef.current) return

      setDbProfile(profile)
      setRole(profile?.role ?? null)
      setUser(Object.assign(authUser, { dbProfile: profile ?? undefined }))

      if (!profile?.role) {
        setTalentProfile(null)
        setOwnerProfile(null)
        setProfileLoading(false)
        return
      }

      if (profile.role === 'talent') {
        const talent = await fetchTalentProfile(authUser.id)
        if (syncVersion !== syncVersionRef.current) return
        setTalentProfile(talent)
        setOwnerProfile(null)
        setProfileLoading(false)
        return
      }

      if (profile.role === 'owner') {
        const owner = await fetchOwnerProfile(authUser.id)
        if (syncVersion !== syncVersionRef.current) return
        setOwnerProfile(owner)
        setTalentProfile(null)
        setProfileLoading(false)
        return
      }

      setTalentProfile(null)
      setOwnerProfile(null)
      setProfileLoading(false)
    },
    [clearProfileState, fetchOwnerProfile, fetchProfile, fetchTalentProfile]
  )

  useEffect(() => {
    let mounted = true

    supabase.auth
      .getSession()
      .then(async ({ data: { session }, error }) => {
        if (error) logSupabaseError('getSession', error)
        if (!mounted) return
        await syncUser(session?.user ?? null, session)
        if (mounted) setLoading(false)
      })
      .catch((err) => {
        log.error('Initial session load failed', err)
        if (mounted) {
          setLoading(false)
          setProfileLoading(false)
        }
      })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      void syncUser(nextSession?.user ?? null, nextSession).finally(() => {
        if (mounted) setLoading(false)
      })
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [syncUser])

  const refreshProfile = useCallback(async () => {
    const currentUser = (await supabase.auth.getUser()).data.user
    if (!currentUser) return
    await syncUser(currentUser, session)
  }, [session, syncUser])

  async function signInWithOtp(email: string) {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: false,
        emailRedirectTo: getAuthConfirmUrl(),
      },
    })
    if (error) {
      logSupabaseError('signInWithOtp', error)
      throw error
    }
  }

  async function verifyOtp(email: string, token: string) {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })
    if (error) {
      logSupabaseError('verifyOtp', error)
      throw error
    }
  }

  async function signInWithPassword(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (error) {
      logSupabaseError('signInWithPassword', error)
      throw error
    }
  }

  async function signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) {
      logSupabaseError('signOut', error)
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        dbProfile,
        talentProfile,
        ownerProfile,
        role,
        loading,
        profileLoading,
        hasProfile,
        signInWithOtp,
        verifyOtp,
        signInWithPassword,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider')
  return ctx
}